import React, { useState, useEffect } from 'react'
import clsx from 'clsx'
import { getSelfSignupModules } from '../../../modules/registry'
import SegmentAndReconstructSVG from '../../svg/SegmentAndReconstructSVG'
import SimulateDevicesSVG from '../../svg/SimulateDevicesSVG'
import LungsSVG from '../../svg/LungsSVG'

/**
 * Step2ModuleSelection Component
 *
 * Second step of signup: User selects modules of interest
 */

const getRecommendedModules = (userType) => {
  const recommendations = {
    learner: [],
    medical: ['segmentation'],
    engineering: ['engineering'],
  }
  return recommendations[userType] || []
}

const Step2ModuleSelection = ({
  userType,
  selectedModules,
  onModulesChange,
  onNext,
  onBack,
}) => {
  const [modules, setModules] = useState([])
  const recommended = getRecommendedModules(userType)

  useEffect(() => {
    // Solo i moduli che il backend accetta in registrazione (non tutto il
    // registry: Vascular e' admin-only e farebbe fallire il signup con 400).
    setModules(getSelfSignupModules())

    // Pre-select recommended modules if nothing selected yet
    if (selectedModules.length === 0) {
      onModulesChange(recommended)
    }
  }, [userType])

  const toggleModule = (moduleId) => {
    if (selectedModules.includes(moduleId)) {
      onModulesChange(selectedModules.filter((m) => m !== moduleId))
    } else {
      onModulesChange([...selectedModules, moduleId])
    }
  }

  const isRecommended = (moduleId) => recommended.includes(moduleId)

  return (
    <div className="max-w-4xl mx-auto">
      <p className="text-center text-white text-opacity-90 mb-6 mt-2">
        Choose the modules you want to use (recommended modules are
        pre-selected)
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {modules.map((module) => {
          const isSelected = selectedModules.includes(module.id)
          const isRec = isRecommended(module.id)
          const Icon =
            module.id === 'segmentation'
              ? SegmentAndReconstructSVG
              : module.id === 'engineering'
                ? SimulateDevicesSVG
                : LungsSVG

          const iconSize = ['segmentation', 'engineering'].includes(module.id) ? 'w-11 h-11 shrink-0' : 'w-9 h-9 shrink-0'

          return (
            <div
              key={module.id}
              className={clsx(
                'relative p-6 border-2 rounded-lg transition-all cursor-pointer hover:shadow-lg flex items-start gap-3',
                {
                  'border-blue-500 bg-blue-900/40': isSelected,
                  'border-white/30 bg-white/5': !isSelected,
                }
              )}
              onClick={() => toggleModule(module.id)}
            >
              <div
                className={clsx('mt-0.5 shrink-0', {
                  'text-blue-500': isSelected,
                  'text-white': !isSelected,
                })}
              >
                <Icon styles={iconSize} isActive={isSelected} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className={clsx('text-lg font-semibold truncate', 'text-white')}>
                    {module.name}
                  </h3>
                  {isRec && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-sm text-white text-opacity-80">
                  {module.description}
                </p>
              </div>
              {isSelected && (
                <div className="absolute top-3 right-3 text-blue-500">
                  {/* checkmark similar to previous page */}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="10"
                      cy="10"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M6 10.5l2.5 2.5L14 7.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {selectedModules.length === 0 && (
        <p className="text-sm text-white text-opacity-80 mb-6">
          Modules can be added later by your administrator.
        </p>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-white/30 rounded-lg text-white hover:bg-white/10 transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          className="px-6 py-3 border border-white/30 rounded-lg text-white hover:bg-white/10 transition-colors"
        >
          Next: Complete Registration →
        </button>
      </div>
    </div>
  )
}

export default Step2ModuleSelection
